import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import CustomDatePicker from "../../commonComponents/CustomDatePicker";
import ValidIcon from "../../assets/icons/emergency_home.svg";
import {
  changeLastWorkingDay,
  uploadFileWithPresignedUrl,
} from "../../store/SeperationSlice/thunk";

// Change the requested (Pending) or approved (Approved) last working day.
// All limits come from summary_view (lwd_min_date / lwd_max_date / lwd_notice_cap);
// the backend re-checks every rule, this form only keeps the user inside them.
const ChangeLwdForm = ({ data, onDismiss }) => {
  const dispatch = useDispatch();
  const fileRef = useRef();
  const [newDate, setNewDate] = useState("");
  const [recoveryDays, setRecoveryDays] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [attachmentKey, setAttachmentKey] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const fileUploadResponse = useSelector(
    (state) => state.seperation.fileUploadResponse,
  );
  const fileUploadResponseStatus = useSelector(
    (state) => state.seperation.fileUploadResponseStatus,
  );

  useEffect(() => {
    if (file && fileUploadResponseStatus === "SUCCESS") {
      setAttachmentKey(fileUploadResponse?.presignedUrlResponse?.fields?.key);
    }
  }, [fileUploadResponseStatus, fileUploadResponse, file]);

  const isApproved = data?.status === "Approved";
  const current = data?.lwd_current;
  const noticeCap = data?.lwd_notice_cap;
  const minDate = data?.lwd_min_date;
  const maxDate = data?.lwd_max_date;
  const maxDays = maxDate
    ? moment(maxDate).startOf("day").diff(moment().startOf("day"), "days")
    : null;

  // Default recovery days = shortfall against the notice period (same formula as
  // the approval screen). The manager can overwrite it, as they can at approval.
  const suggestedRecoveryDays = useMemo(() => {
    if (!newDate || !noticeCap) return 0;
    return Math.max(moment(noticeCap).diff(moment(newDate), "days"), 0);
  }, [newDate, noticeCap]);
  const newRecoveryDays = recoveryDays === "" ? 0 : Number(recoveryDays);
  const recoveryMissing = isApproved && newDate && recoveryDays === "";

  const attachmentRequired =
    data?.lwd_attachment_needed_if_recovery && newRecoveryDays > 0;
  const dateOutOfRange =
    newDate &&
    (moment(newDate).isBefore(minDate, "day") ||
      moment(newDate).isAfter(maxDate, "day"));
  const sameAsCurrent = newDate && moment(newDate).isSame(current, "day");
  const remarksTooShort = remarks.trim().length < 5;
  const canSubmit =
    newDate &&
    !dateOutOfRange &&
    !sameAsCurrent &&
    !recoveryMissing &&
    !remarksTooShort &&
    (!attachmentRequired || attachmentKey);

  const fmt = (d) => (d ? moment(d).format("DD MMMM YYYY") : "-");

  const onPickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setAttachmentKey("");
    dispatch(
      uploadFileWithPresignedUrl({
        employee_id: data?.employee_id,
        file: f,
        key: "seperation",
      }),
    );
  };

  const onSubmit = () => {
    setShowErrors(true);
    if (!canSubmit) return;
    dispatch(
      changeLastWorkingDay({
        employee_id: data?.employee_id,
        new_last_working_day: moment(newDate).format("YYYY-MM-DD"),
        recovery_days: isApproved ? newRecoveryDays : null,
        remarks: remarks.trim(),
        attachments: attachmentKey || null,
      }),
    );
  };

  return (
    <div className="sp-confir">
      <div className="sp-cf-head">
        change {isApproved ? "final date of exit" : "requested last working day"}
      </div>
      <div className="sp-cf-subhead">
        {isApproved
          ? "This is the approved exit date. Attendance, system access, device sync and the clearance task timelines all follow it."
          : "The manager will still confirm the final exit date at approval."}
      </div>

      <div className="sp-cf-content">
        <div className="sp-cf-label">Current {isApproved ? "final date of exit" : "requested date"}</div>
        <div className="sp-cf-value">{fmt(current)}</div>
      </div>
      {isApproved && data?.req_date_of_exit && (
        <div className="sp-cf-content">
          <div className="sp-cf-label">Date the employee requested</div>
          <div className="sp-cf-value">{fmt(data?.req_date_of_exit)}</div>
        </div>
      )}
      <div className="sp-cf-content">
        <div className="sp-cf-label">Allowed range</div>
        <div className="sp-cf-value">
          {fmt(minDate)} to {fmt(maxDate)}
          {noticeCap
            ? ` (notice period ends ${fmt(noticeCap)})`
            : " (no notice period on file - can only be brought earlier)"}
        </div>
      </div>

      <div className="flex flex-col w-full" style={{ padding: "16px 0 0" }}>
        <CustomDatePicker
          isRequired={true}
          label={`New ${isApproved ? "final date of exit" : "requested last working day"}`}
          value={newDate}
          onChange={(value) => {
            const d = value ? moment(value).format("YYYY-MM-DD") : "";
            setNewDate(d);
            setRecoveryDays(
              d && noticeCap
                ? String(Math.max(moment(noticeCap).diff(moment(d), "days"), 0))
                : d ? "0" : "",
            );
          }}
          placeholderText={"Select a date"}
          allowPastDate={false}
          allowFutureDate={maxDays === null || maxDays > 0}
          minAllowedDate={minDate}
          maxAllowedDate={maxDays && maxDays > 0 ? maxDays : null}
          isError={showErrors && !newDate}
        />
        {dateOutOfRange && (
          <div className="valid-text flex items-center">
            <img src={ValidIcon} alt="" style={{ marginRight: "4px" }} />
            <div>
              Date must be between {fmt(minDate)} and {fmt(maxDate)} - not in
              the past and not beyond the notice period.
            </div>
          </div>
        )}
        {sameAsCurrent && (
          <div className="valid-text flex items-center">
            <img src={ValidIcon} alt="" style={{ marginRight: "4px" }} />
            <div>That is already the current date. Pick a different one.</div>
          </div>
        )}
      </div>

      {isApproved && newDate && !dateOutOfRange && (
        <div className="flex flex-col w-full" style={{ padding: "16px 0 0" }}>
          <div className="emp-form-label">Final Recovery Days*</div>
          <input
            className={`emp-form-input ${
              showErrors && recoveryMissing ? "error-border" : ""
            }`}
            type="number"
            min="0"
            value={recoveryDays}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") return setRecoveryDays("");
              if (Number(v) < 0) return;
              setRecoveryDays(String(Math.floor(Number(v))));
            }}
          />
          <div className="sp-cf-subhead" style={{ marginTop: "4px" }}>
            Currently {data?.recovery_days ?? 0} days. Suggested{" "}
            {suggestedRecoveryDays} days (notice period ends {fmt(noticeCap)}
            ); change it if a different recovery was agreed.
          </div>
        </div>
      )}

      <div className="flex flex-col w-full" style={{ padding: "16px 0 0" }}>
        <div className="emp-form-label">Reason for change*</div>
        <textarea
          className={`emp-form-input ${
            showErrors && remarksTooShort ? "error-border" : ""
          }`}
          rows={3}
          style={{ height: "auto", padding: "10px" }}
          placeholder="e.g. Negotiated early release after handover / extended for knowledge transfer"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        {showErrors && remarksTooShort && (
          <div className="valid-text flex items-center">
            <img src={ValidIcon} alt="" style={{ marginRight: "4px" }} />
            <div>Please give a reason for the change (at least a few words).</div>
          </div>
        )}
      </div>

      {attachmentRequired && (
        <div className="flex flex-col w-full" style={{ padding: "16px 0 0" }}>
          <div className="emp-form-label">
            Early release approval / waiver document*
          </div>
          <div className="sp-cf-subhead" style={{ marginBottom: "6px" }}>
            {newRecoveryDays} recovery days will apply. As at manager approval,
            an early release needs the approval document attached.
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".jpeg,.png,.pdf,.jpg"
            className={`emp-form-input ${
              showErrors && !attachmentKey ? "error-border" : ""
            }`}
            style={{ padding: "8px" }}
            onChange={onPickFile}
          />
          {file && !attachmentKey && (
            <div className="sp-cf-subhead">Uploading {file.name}...</div>
          )}
          {attachmentKey && (
            <div className="sp-cf-subhead">Attached: {file?.name}</div>
          )}
        </div>
      )}

      <div className="sp-cf-btns" style={{ marginTop: "16px" }}>
        <button
          className="sp-cf-submit"
          onClick={onSubmit}
          disabled={showErrors && !canSubmit}
        >
          Change Last Working Day
        </button>
        <button className="sp-cf-dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default ChangeLwdForm;
